// React Dependencies
import React, { useState, useRef, useCallback } from 'react';

// External Dependencies
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import MaterialTable from 'material-table';
import {
  FormControl,
  TextField,
} from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { toast } from 'react-toastify';

// Internal Dependencies
import apiService from '../../services.js/apiService';
import DateUtil from '../../common/dateUtil';
import { updateModalContent, toggleModal } from '../../actions/modal';
import MButton from '../common/MButton';
import MText from '../common/MText';
import Colours from '../../styles/colours';
import StatusBadge from './statusBadge';
import { IsEmpty } from '../../common/checks';
import { ModerationTypes } from '../../common/constants';
import { Trim } from '../../common/util';
import UnbanModal from './UnbanModal';
import BanModal from './BanModal';

const detailRows = [
  { label: 'Shopper ID',     key: 'id' },
  { label: 'Username',       key: 'username' },
  { label: 'Email',          key: 'email' },
  { label: 'Banned At',      key: 'bannedAt',     date: true },
  { label: 'Banned By',      key: 'bannedBy' },
  { label: 'Banned Reason',  key: 'bannedReason' },
];

const formatValue = (user, row) => {
  const value = user[row.key];
  if (IsEmpty(value)) {
    return '-';
  }

  if (!IsEmpty(row.date)) {
    return DateUtil.FormatLocalDateAndTime(value);
  }

  return value;
};

const UserModerationTable = (props) => {
  const table = useRef(null);
  const searchKeyRef = useRef('');

  const [searchKey, setSearchKey]       = useState('');
  const [loading, setLoading]           = useState(false);

  const [unbanConfirmOpen, setUnbanConfirmOpen] = useState(false);
  const [targetUser, setTargetUser]     = useState(null);

  const [pageSize, setPageSize]         = useState(20);

  // Ban Modal State
  const [banModalOpen, setBanModalOpen] = useState(false);

  const openUnbanConfirm = (user) => {
    setTargetUser(user);
    setUnbanConfirmOpen(true);
  };

  const refreshTable = () => {
    if (!IsEmpty(table.current)) {
      table.current.onQueryChange();
    }
  };

  const handleViewUserDetails = async (user) => {
    try {
      const res = await apiService.getShopperModerationDetails({ id: user.id });
      if (IsEmpty(res.rows)) {
        toast.error('Shopper not found');
        return;
      }
      const details = res.rows[0];

      props.updateModalContent({
        title: (
          <div className="d-flex align-items-center mt-2 mx-2" style={{ gap: 12 }}>
            <MText text="User Details" size={16} bold />
            <StatusBadge isBanned={details.isBanned} />
          </div>
        ),
        content: (
          <div className="mx-2">
            <div className="py-2">
              {detailRows.map((row) => (
                <div className="row mb-2" key={row.key}>
                  <div className="col-auto" style={{ width: 160 }}>
                    <MText text={row.label} colour="#666" size={14} bold />
                  </div>
                  <div className="col" style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    <MText text={String(formatValue(details, row))} size={14} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ),
        cancelText:       'Close',
        closeAfterAction: true,
        centered:         true,
        width:            'sm',
      });
      props.toggleModal();
    } catch (err) {
      toast.error(err.message || String(err));
    }
  };

  const columns = [
    { title: 'ID',       field: ModerationTypes.Id,       filtering: false, width: '5%' },
    {
      title:     'Username',
      field:     ModerationTypes.Username,
      filtering: false,
      render:    (rowData) => (
        <MButton
          text={rowData.username || '-'}
          variant="text"
          textColour={Colours.Blue}
          onClick={() => handleViewUserDetails(rowData)}
        />
      ),
    },
    { title: 'Email',    field: ModerationTypes.Email,    filtering: false, render: (r) => <MText text={r.email || '-'} /> },
    ...([{
      title:     '',
      field:     'action',
      filtering: false,
      sorting:   false,
      cellStyle: { width: '15%' },
      render:    (rowData) => (
        <MButton
          text="Unban"
          variant="outlined"
          textColour={Colours.Blue}
          style={{ borderColor: Colours.Blue, fontSize: 12, padding: '4px 12px' }}
          onClick={() => openUnbanConfirm(rowData)}
        />
      ),
    }]),
  ];

  const fetchDataTable = useCallback(async (query) => {
    try {
      setLoading(true);
      const params = {
        pageNo:   query.page,
        pageSize: query.pageSize,
      };

      const search = Trim(searchKeyRef.current);
      if (!IsEmpty(search)) {
        params.key = search;
      }

      const result = await apiService.getBannedShoppers(params);

      const { rows, totalCount } = result;
      const { page } = query;

      return {
        data: rows,
        page,
        totalCount,
      };
    } catch (err) {
      toast.error(err.message);
      return {
        data:       [],
        page:       0,
        totalCount: 0,
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSearch = () => {
    searchKeyRef.current = searchKey;
    if (table.current) {
      table.current.onQueryChange();
    }
  };

  const handleSearchKeyChange = (e) => {
    searchKeyRef.current = e.target.value;
    setSearchKey(e.target.value);
  };

  const clearFilters = () => {
    searchKeyRef.current = '';
    setSearchKey('');
    setTimeout(() => {
      if (table.current) {
        table.current.onQueryChange();
      }
    }, 0);
  };

  return (
    <div>
      <div className="pt-3">
        <Box display="flex" justifyContent="space-between" alignItems="center" m={0} p={1} bgcolor="background.paper">
          <Box display="flex" flexWrap="wrap" justifyContent="flex-start">
            <Box p={1} style={{ flex: '1 1 200px', minWidth: 200, maxWidth: 360 }}>
              <FormControl fullWidth>
                <TextField
                  name="banned-shopper-table-search"
                  label="Username, email, or ID"
                  value={searchKey}
                  onChange={handleSearchKeyChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
              </FormControl>
            </Box>

            <Box p={1} width="auto" className="d-flex align-items-center">
              <FormControl fullWidth>
                <MButton
                  variant="contained"
                  className="mt-3"
                  colour={Colours.Primary}
                  textColour={Colours.White}
                  onClick={handleSearch}
                  text="Search"
                />
              </FormControl>
            </Box>

            <Box p={1} width="auto" className="d-flex align-items-center">
              <FormControl fullWidth>
                <MButton
                  variant="outlined"
                  className="mt-3"
                  textColour={Colours.Primary}
                  onClick={clearFilters}
                  text="Clear Filters"
                />
              </FormControl>
            </Box>
          </Box>

          <Box p={1}>
            <MButton
              variant="contained"
              colour={Colours.Crimson}
              textColour={Colours.White}
              onClick={() => setBanModalOpen(true)}
              text="Ban a Shopper"
            />
          </Box>
        </Box>
      </div>

      <MaterialTable
        tableRef={table}
        className="material-table"
        title=""
        columns={columns}
        onChangeRowsPerPage={(size) => {
          setPageSize(size);
        }}
        isLoading={loading}
        data={fetchDataTable}
        options={{
          pageSize,
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
        }}
      />

      <UnbanModal
        open={unbanConfirmOpen}
        user={targetUser}
        onClose={() => setUnbanConfirmOpen(false)}
        onSuccess={() => {
          setUnbanConfirmOpen(false);
          refreshTable();
        }}
      />

      <BanModal
        open={banModalOpen}
        onClose={() => setBanModalOpen(false)}
        onSuccess={() => {
          setBanModalOpen(false);
          refreshTable();
        }}
      />
    </div>
  );
};

UserModerationTable.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

// map the redux action creators to props
const mapActionToProps = {
  updateModalContent,
  toggleModal,
};

export default connect(null, mapActionToProps)(UserModerationTable);
